import type { ICMSLottery } from 'api/cms/lottery';
import type { Lottery } from 'api/lotteries';
import { LotteriesContext } from 'contexts/lotteries';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  getLotteryPhase,
  getNextPhaseChange,
  pickActiveLottery,
  setTimeoutAt,
} from '../helpers/phase';

// the next lottery may already be coming soon while the previous one is live
const CANDIDATES_COUNT = 2;

interface ICandidate {
  cms: ICMSLottery;
  lottery: Lottery;
}

const getNextShowFrom = (cmsLotteries: ICMSLottery[], now: number) =>
  cmsLotteries
    .map(({ showFrom }) => Date.parse(showFrom))
    .find((time) => time > now) ?? null;

/** the CMS picks the lotteries by `show_from`, SS gives their dates and state */
export const useCurrentLottery = () => {
  const {
    lotteries,
    lotteriesPending,
    cmsLotteries,
    cmsLotteriesPending,
    refreshLottery,
  } = useContext(LotteriesContext);
  const [now, setNow] = useState(Date.now);
  const [settledIds, setSettledIds] = useState<number[]>([]);
  const requestedIds = useRef(new Set<number>());
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const shown = useMemo(
    () =>
      cmsLotteries
        .filter(({ showFrom }) => Date.parse(showFrom) <= now)
        .slice(-CANDIDATES_COUNT),
    [cmsLotteries, now]
  );

  const candidates = useMemo(
    () =>
      shown.flatMap<ICandidate>((cms) => {
        const lottery = lotteries.find(({ id }) => id === cms.ssLotteryId);

        return lottery ? [{ cms, lottery }] : [];
      }),
    [shown, lotteries]
  );

  // a drawn lottery leaves the enabled list, it's reachable by id only
  const missingIds = useMemo(
    () =>
      shown
        .map(({ ssLotteryId }) => ssLotteryId)
        .filter((id) => !candidates.some(({ lottery }) => lottery.id === id)),
    [shown, candidates]
  );

  useEffect(() => {
    if (lotteriesPending) return;

    for (const id of missingIds) {
      if (requestedIds.current.has(id)) continue;

      requestedIds.current.add(id);
      refreshLottery(id).finally(() => {
        if (isMountedRef.current) setSettledIds((ids) => [...ids, id]);
      });
    }
  }, [lotteriesPending, missingIds, refreshLottery]);

  useEffect(() => {
    const changes = [
      getNextPhaseChange(
        candidates.map(({ lottery }) => lottery),
        now
      ),
      getNextShowFrom(cmsLotteries, now),
    ].filter((time): time is number => time !== null);

    if (!changes.length) return;

    const timeout = setTimeoutAt(Math.min(...changes), () =>
      setNow(Date.now())
    );

    return () => clearTimeout(timeout);
  }, [candidates, cmsLotteries, now]);

  const current = useMemo(() => {
    const active = pickActiveLottery(
      candidates.map(({ lottery }) => lottery),
      now
    );

    if (active) {
      const cms = candidates.find(
        ({ lottery }) => lottery.id === active.lottery.id
      )?.cms;

      return cms ? { ...active, cms } : null;
    }

    // nothing live or coming: the latest one stays until the next shows up
    const latest = candidates[candidates.length - 1];

    return latest
      ? { ...latest, phase: getLotteryPhase(latest.lottery, now) }
      : null;
  }, [candidates, now]);

  return {
    lottery: current?.lottery ?? null,
    cmsLottery: current?.cms ?? null,
    phase: current?.phase ?? null,
    pending:
      cmsLotteriesPending ||
      lotteriesPending ||
      missingIds.some((id) => !settledIds.includes(id)),
  };
};
